import type { Emitter } from "@core/events/emitter";

/**
 * `ICustomerModel` — web port of Unity's `Models/CustomerModel/ICustomerModel.cs`,
 * trimmed to what the web port currently consumes (the projects-list
 * header today; entitlement / role checks land later).
 *
 * Unity sources the customer / company name from the GraphQL
 * `getAccount(accountid: …)` admin query (`AwsAdminStorageModel.
 * SendCompanyGraphQlRequest`) and exposes it via `CustomerInfo.Name`.
 * We expose the same string under `CustomerInfo.name` here.
 */
export interface CustomerInfo {
  /** Account / company id (`custom:accountid` Cognito claim). */
  readonly customerId: string;
  /** Display name from the `getAccount` GraphQL `name` field. */
  readonly name: string;
  /** `CampfirePlanType` int — see `campfirePlanType.ts` (values are sparse). */
  readonly planType: number;
  /** `CustomerData.IsPaidAccount` — note EnterpriseFree counts as PAID. */
  readonly isPaidAccount: boolean;
  /**
   * The account's sharing rules (`CustomerData.ShareAnyone` / `ShareDomains`
   * / `SafeDomains`, `AwsAdminStorageModel.cs:1150-1161`), read by
   * `SharedUsersModel.canInviteUser`: a paid account may invite anyone when
   * `shareAnyone` is on, or anyone whose email ends in one of `safeDomains`
   * when `shareDomains` is on. `safeDomains` is the GraphQL `safeDomains`
   * string split on `,`, entries untrimmed exactly as Unity keeps them (it
   * trims at COMPARE time, `SharedUsersModel.cs:440`).
   */
  readonly shareAnyone: boolean;
  readonly shareDomains: boolean;
  readonly safeDomains: ReadonlyArray<string>;
}

export interface ICustomerModel {
  /** Most recently loaded customer, or `null` before the first load. */
  readonly customerInfo: CustomerInfo | null;

  /** Emitted whenever `customerInfo` changes (load, sign-out, switch). */
  readonly onChanged: Emitter<CustomerInfo | null>;

  /**
   * Load (or refresh) the current customer. Mirrors Unity's
   * `CustomerModel.GetCustomerAsync()`. Resolves with the customer
   * info or `null` when the user is not signed in / the request
   * fails (the UI should fall back, never throw).
   */
  getCustomerAsync(signal?: AbortSignal): Promise<CustomerInfo | null>;

  /** Drop cached state on sign-out / env switch. */
  reset(): void;
}

export const CUSTOMER_MODEL = Symbol.for("Campfire.CustomerModel");
